// Core
import React from 'react';

// Libraries
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

// Plugins
import {
  booleanStatusToIndicatorStatus,
} from '../../../../../../../../plugins/crusher';
import { DASHBOARD_NULLISH_TEXT } from '../../../../../../../../plugins/general';

// Components
import {
  HighlightCard,
} from '../../../../../../../../components/HighlightCard';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../../../layout/elements/Paragraph';
import { StatusIndicator } from '../../../../../../../../layout/elements/StatusIndicator';

// View
import {
  Container,
  DiagnosticContainer,
  HighlightCardsContainer,
  NetworkCheckContainer,
  NetworkCheckHeaderContainer,
  NetworkCheckSection,
  NetworkCheckTitleContainer,
  NetworkContainer,
  NetworkItemsContainer,
  NetworkParagraphContainer,
  NetworkStatusContainer,
  NetworkStatusIndicatorsContainer,
  NetworkStatusSection,
  StatusContainer,
  StatusIndicatorContainer,
} from './ASINetwork.style';
import { ASINetworkProps } from './ASINetwork.types';

function ASINetwork({
  className,
  unitData,
  edgeStatus,
  testId,
}: ASINetworkProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  /* ***********************************************************************************************
   ******************************************* METHODS *********************************************
   ********************************************************************************************** */

  /**
   * Renders a network status item with an indicator and text.
   *
   * @param {string} statusName - The status name for the status text.
   * @param {boolean | null | undefined} status - The status value from operational data.
   * @returns {JSX.Element} The rendered network status item.
  */
  const renderNetworkStatus = (
    statusName: string,
    status: boolean | null | undefined,
  ): JSX.Element => (
    <StatusContainer>
      <StatusIndicatorContainer>
        <StatusIndicator
          status={booleanStatusToIndicatorStatus(
            status,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      </StatusIndicatorContainer>
      <Paragraph
        size={ParagraphSize.sm}
        weight={ParagraphWeight.medium}
      >
        {t(`view.crusher.maintenance.ifm.asiNetwork.${statusName}`)}
      </Paragraph>
    </StatusContainer>
  );

  /**
   * Renders a network check item with status indicators and text.
   *
   * @param {string} itemId - The ID of the network item (e.g., "1A", "1B", etc.).
   * @param {string} location - The location text for the network item.
   * @param {boolean | null | undefined} notProjectedStatus - Network not projected status
   * @param {boolean | null | undefined} duplicateAddressStatus - Duplicate address status
   * @param {boolean | null | undefined} peripheryStatus - Network periphery status
   * @param {boolean | null | undefined} missingAddressStatus - Missing address status
   * @param {boolean | null | undefined} networkProfileStatus - Network profile status
   * @returns {JSX.Element} The rendered network check item.
  */
  const renderNetworkCheck = (
    itemId: string,
    location: string,
    notProjectedStatus: boolean | null | undefined,
    duplicateAddressStatus: boolean | null | undefined,
    peripheryStatus: boolean | null | undefined,
    missingAddressStatus: boolean | null | undefined,
    networkProfileStatus: boolean | null | undefined,
  ): JSX.Element => (
    <NetworkItemsContainer>
      <NetworkParagraphContainer>
        <Paragraph
          size={ParagraphSize.sm}
          weight={ParagraphWeight.medium}
        >
          {itemId}
        </Paragraph>
      </NetworkParagraphContainer>

      <NetworkParagraphContainer>
        <Paragraph
          size={ParagraphSize.sm}
          weight={ParagraphWeight.medium}
        >
          {location}
        </Paragraph>
      </NetworkParagraphContainer>

      <NetworkStatusIndicatorsContainer>
        <StatusIndicator
          status={booleanStatusToIndicatorStatus(
            notProjectedStatus,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      </NetworkStatusIndicatorsContainer>

      <NetworkStatusIndicatorsContainer>
        <StatusIndicator
          status={booleanStatusToIndicatorStatus(
            duplicateAddressStatus,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      </NetworkStatusIndicatorsContainer>

      <NetworkStatusIndicatorsContainer>
        <StatusIndicator
          status={booleanStatusToIndicatorStatus(
            peripheryStatus,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      </NetworkStatusIndicatorsContainer>

      <NetworkStatusIndicatorsContainer>
        <StatusIndicator
          status={booleanStatusToIndicatorStatus(
            missingAddressStatus,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      </NetworkStatusIndicatorsContainer>

      <NetworkStatusIndicatorsContainer>
        <StatusIndicator
          status={booleanStatusToIndicatorStatus(
            networkProfileStatus,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      </NetworkStatusIndicatorsContainer>
    </NetworkItemsContainer>
  );

  /* ***********************************************************************************************
  ************************************** COMPONENT HANDLING ****************************************
  *********************************************************************************************** */

  /**
   * Renders the highlight cards for ASI voltage and symmetry data
   *
   * @returns {JSX.Element} The rendered highlight cards
   */
  const renderHighlightCards = (): JSX.Element => (
    <HighlightCardsContainer>
      <HighlightCard
        text={t('view.crusher.maintenance.ifm.asiNetwork.asiVoltageFe')}
        // status={numberToHighlightCardStatus( // @TODO: uncomment when to display status
        //   unitData?.ifmAsiVoltageFE,
        //   lastEdgeStatus?.edgeStatus,
        //   unitData?.timestamp,
        // )}
        value={
          _.isNil(unitData?.ifmAsiVoltageFE)
            ? DASHBOARD_NULLISH_TEXT
            : unitData?.ifmAsiVoltageFE
          }
        unit="VDC"
      />
      <HighlightCard
        text={t('view.crusher.maintenance.ifm.asiNetwork.asiVoltageVdc')}
        // status={numberToHighlightCardStatus( // @TODO: uncomment when to display status
        //   unitData?.ifmAsiVoltageVDC,
        //   lastEdgeStatus?.edgeStatus,
        //   unitData?.timestamp,
        // )}
        value={
          _.isNil(unitData?.ifmAsiVoltageVDC)
            ? DASHBOARD_NULLISH_TEXT
            : unitData?.ifmAsiVoltageVDC
        }
        unit="VDC"
      />
      <HighlightCard
        text={t('view.crusher.maintenance.ifm.asiNetwork.asiSymmetry')}
        // status={numberToHighlightCardStatus( // @TODO: uncomment when to display status
        //   unitData?.ifmAsiSymmetry,
        //   lastEdgeStatus?.edgeStatus,
        //   unitData?.timestamp,
        // )}
        value={
          _.isNil(unitData?.ifmAsiSymmetry)
            ? DASHBOARD_NULLISH_TEXT
            : unitData?.ifmAsiSymmetry
        }
        unit="%"
      />
    </HighlightCardsContainer>
  );

  return (
    <Container
      className={className}
      data-testid={testId}
    >
      {renderHighlightCards()}
      <NetworkContainer>
        <NetworkStatusSection>
          <NetworkStatusContainer>
            <Paragraph
              size={ParagraphSize.lg}
              weight={ParagraphWeight.bold}
            >
              {t('view.crusher.maintenance.ifm.asiNetwork.networkStatus')}
            </Paragraph>
          </NetworkStatusContainer>
          <DiagnosticContainer>
            <Paragraph
              size={ParagraphSize.sm}
              weight={ParagraphWeight.bold}
            >
              Status
            </Paragraph>
            <Paragraph
              size={ParagraphSize.sm}
              weight={ParagraphWeight.bold}
            >
              {t('view.crusher.maintenance.ifm.asiNetwork.diagnostic')}
            </Paragraph>
          </DiagnosticContainer>

          {renderNetworkStatus(
            'additionalNode',
            unitData?.ifmAdditionalNode,
          )}

          {renderNetworkStatus(
            'invalidProfile',
            unitData?.ifmInvalidProfile,
          )}

          {renderNetworkStatus(
            'missingNode',
            unitData?.ifmMissingNode,
          )}

          {renderNetworkStatus(
            'earthFault',
            unitData?.ifmEarthFault,
          )}

          {renderNetworkStatus(
            'gatewayModeIsDeactivated',
            unitData?.ifmGatewayInactive,
          )}

          {renderNetworkStatus(
            'internalSystemError',
            unitData?.ifmInternalError,
          )}

          {renderNetworkStatus(
            'tempInDeviceHasReachedLimit',
            unitData?.ifmTempLimit,
          )}

          {renderNetworkStatus(
            'asiPowerFail19V',
            unitData?.ifm19AsiPowerFail,
          )}

          {renderNetworkStatus(
            'asiPowerFail22,5V',
            unitData?.ifm225PowerFail,
          )}
        </NetworkStatusSection>

        <NetworkCheckSection>
          <NetworkCheckContainer>
            <NetworkCheckTitleContainer>
              <Paragraph
                size={ParagraphSize.lg}
                weight={ParagraphWeight.bold}
              >
                {t('view.crusher.maintenance.ifm.asiNetwork.networkCheck')}
              </Paragraph>
            </NetworkCheckTitleContainer>
            <NetworkCheckHeaderContainer>
              <Paragraph
                size={ParagraphSize.sm}
                weight={ParagraphWeight.bold}
              >
                {t('view.crusher.maintenance.ifm.asiNetwork.moduleAddress')}
              </Paragraph>
              <Paragraph
                size={ParagraphSize.sm}
                weight={ParagraphWeight.bold}
              >
                {t('view.crusher.maintenance.ifm.asiNetwork.moduleLocation')}
              </Paragraph>
              <Paragraph
                size={ParagraphSize.sm}
                weight={ParagraphWeight.bold}
              >
                {t('view.crusher.maintenance.ifm.asiNetwork.netWorkNotProtected')}
              </Paragraph>
              <Paragraph
                size={ParagraphSize.sm}
                weight={ParagraphWeight.bold}
              >
                {t('view.crusher.maintenance.ifm.asiNetwork.duplicateAddress')}
              </Paragraph>
              <Paragraph
                size={ParagraphSize.sm}
                weight={ParagraphWeight.bold}
              >
                {t('view.crusher.maintenance.ifm.asiNetwork.networkPeriphery')}
              </Paragraph>
              <Paragraph
                size={ParagraphSize.sm}
                weight={ParagraphWeight.bold}
              >
                {t('view.crusher.maintenance.ifm.asiNetwork.missingAddress')}
              </Paragraph>
              <Paragraph
                size={ParagraphSize.sm}
                weight={ParagraphWeight.bold}
              >
                {t('view.crusher.maintenance.ifm.asiNetwork.networkProfileAddress')}
              </Paragraph>
            </NetworkCheckHeaderContainer>

            {/* 1A */}
            {renderNetworkCheck(
              '1A',
              'Combo / HPU',
              unitData?.ifmNetworkNotProjected1A,
              unitData?.ifmDuplicateAddress1A,
              unitData?.ifmNetworkPeriphery1A,
              unitData?.ifmMissingAddress1A,
              unitData?.ifmNetworkProfile1A,
            )}
            {renderNetworkCheck(
              '1B',
              'Combo / HPU',
              unitData?.ifmNetworkNotProjected1B,
              unitData?.ifmDuplicateAddress1B,
              unitData?.ifmNetworkPeriphery1B,
              unitData?.ifmMissingAddress1B,
              unitData?.ifmNetworkProfile1B,
            )}
            {renderNetworkCheck(
              '2',
              'Combo / HPU',
              unitData?.ifmNetworkNotProjected2,
              unitData?.ifmDuplicateAddress2,
              unitData?.ifmNetworkPeriphery2,
              unitData?.ifmMissingAddress2,
              unitData?.ifmNetworkProfile2,
            )}
            {renderNetworkCheck(
              '7',
              t('view.crusher.maintenance.ifm.asiNetwork.comboLubrication'),
              unitData?.ifmNetworkNotProjected7,
              unitData?.ifmDuplicateAddress7,
              unitData?.ifmNetworkPeriphery7,
              unitData?.ifmMissingAddress7,
              unitData?.ifmNetworkProfile7,
            )}
            {renderNetworkCheck(
              '8',
              t('view.crusher.maintenance.ifm.asiNetwork.comboLubrication'),
              unitData?.ifmNetworkNotProjected8,
              unitData?.ifmDuplicateAddress8,
              unitData?.ifmNetworkPeriphery8,
              unitData?.ifmMissingAddress8,
              unitData?.ifmNetworkProfile8,
            )}
            {renderNetworkCheck(
              '9',
              t('view.crusher.maintenance.ifm.asiNetwork.comboLubrication'),
              unitData?.ifmNetworkNotProjected9,
              unitData?.ifmDuplicateAddress9,
              unitData?.ifmNetworkPeriphery9,
              unitData?.ifmMissingAddress9,
              unitData?.ifmNetworkProfile9,
            )}
            {renderNetworkCheck(
              '10',
              t('view.crusher.maintenance.ifm.asiNetwork.crusherJBox'),
              unitData?.ifmNetworkNotProjected10,
              unitData?.ifmDuplicateAddress10,
              unitData?.ifmNetworkPeriphery10,
              unitData?.ifmMissingAddress10,
              unitData?.ifmNetworkProfile10,
            )}
            {renderNetworkCheck(
              '11',
              t('view.crusher.maintenance.ifm.asiNetwork.crusherJBox'),
              unitData?.ifmNetworkNotProjected11,
              unitData?.ifmDuplicateAddress11,
              unitData?.ifmNetworkPeriphery11,
              unitData?.ifmMissingAddress11,
              unitData?.ifmNetworkProfile11,
            )}
            {renderNetworkCheck(
              '12',
              t('view.crusher.maintenance.ifm.asiNetwork.crusherJBox'),
              unitData?.ifmNetworkNotProjected12,
              unitData?.ifmDuplicateAddress12,
              unitData?.ifmNetworkPeriphery12,
              unitData?.ifmMissingAddress12,
              unitData?.ifmNetworkProfile12,
            )}
            {renderNetworkCheck(
              '13',
              t('view.crusher.maintenance.ifm.asiNetwork.crusherJBox'),
              unitData?.ifmNetworkNotProjected13,
              unitData?.ifmDuplicateAddress13,
              unitData?.ifmNetworkPeriphery13,
              unitData?.ifmMissingAddress13,
              unitData?.ifmNetworkProfile13,
            )}
            {renderNetworkCheck(
              '14',
              t('view.crusher.maintenance.ifm.asiNetwork.crusherJBox'),
              unitData?.ifmNetworkNotProjected14,
              unitData?.ifmDuplicateAddress14,
              unitData?.ifmNetworkPeriphery14,
              unitData?.ifmMissingAddress14,
              unitData?.ifmNetworkProfile14,
            )}
            {renderNetworkCheck(
              '15',
              t('view.crusher.maintenance.ifm.asiNetwork.crusherJBox'),
              unitData?.ifmNetworkNotProjected15,
              unitData?.ifmDuplicateAddress15,
              unitData?.ifmNetworkPeriphery15,
              unitData?.ifmMissingAddress15,
              unitData?.ifmNetworkProfile15,
            )}
          </NetworkCheckContainer>
        </NetworkCheckSection>
      </NetworkContainer>
    </Container>
  );
}

export { ASINetwork };
